import { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@/UI/Trans/Trans';
import { WEAVY_PAYWALL_ENTRYPOINTS } from '@/consts/weavyPaywallEntrypoints.consts';
import { PricingPageEntrypoint } from '@/enums/pricing-page.enum';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { SubscriptionType } from '@/types/shared';
import { AuthContext } from '@/contexts/AuthContext';
import { refreshSubscriptionClaimAfterUpgrade } from '@/services/auth/subscription-type-claim';
import { log } from '@/logger/logger';
import { sanitizeError } from '@/utils/sanitize-error';
import type { ReactNode } from 'react';

const logger = log.getLogger('PaymentSuccessHandler');

const enum PaymentType {
  CREDITS = 'credits',
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
  UNKNOWN = 'unknown',
}

const isSubscriptionType = (value: string): value is SubscriptionType =>
  Object.values(SubscriptionType).includes(value as SubscriptionType);

const knownEntrypoint = (value: string | null) =>
  value &&
  (Object.hasOwn(WEAVY_PAYWALL_ENTRYPOINTS, value) || Object.values<string>(PricingPageEntrypoint).includes(value))
    ? value
    : undefined;

function PaymentSuccessHandler() {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { authApi } = useContext(AuthContext);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const hasTrackedSuccess = useRef(false);

  const getSuccessMessage = useCallback((paymentType: PaymentType, amount: string, plan: string): ReactNode => {
    switch (paymentType) {
      case PaymentType.CREDITS:
        return (
          <Trans
            i18nKey={I18N_KEYS.PAYMENTS.CREDITS_SUCCESS_MESSAGE}
            components={{
              amount: <span style={{ fontWeight: 'bold' }}>{(Number(amount) || '').toLocaleString()}</span>,
            }}
          />
        );
      case PaymentType.UPGRADE:
        return (
          <Trans
            i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_SUCCESS_MESSAGE}
            components={{
              plan: <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plan}</span>,
            }}
          />
        );
      case PaymentType.DOWNGRADE:
        return (
          <Trans
            i18nKey={I18N_KEYS.PAYMENTS.DOWNGRADE_SUCCESS_MESSAGE}
            components={{
              plan: <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{plan}</span>,
            }}
          />
        );
      default:
        return 'Awesome! Your payment was successful.';
    }
  }, []);

  const raiseSnackbar = useCallback(
    (successMessage: ReactNode) => {
      updateSnackbarData({
        text: successMessage,
        isOpen: true,
        severity: 'success',
        icon: null,
        duration: 5000,
        action: null,
        showCloseButton: true,
      });
    },
    [updateSnackbarData],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('session_id')) {
      const paymentType = (urlParams.get('payment') as PaymentType | null) ?? PaymentType.UNKNOWN; // upgrade plan or buy credits
      const plan = (urlParams.get('plan') as SubscriptionType | null) ?? 'unknown';
      const amount = urlParams.get('amount') || 'unknown';

      if (paymentType === PaymentType.UPGRADE && isSubscriptionType(plan)) {
        const authUser = authApi.getCurrentUser();
        if (authUser) {
          void refreshSubscriptionClaimAfterUpgrade(authUser, plan)
            .then((didRefresh) => {
              if (!didRefresh) {
                logger.warn('Subscription claim did not update before retries were exhausted', {
                  userId: authUser.uid,
                  expectedSubscription: plan,
                });
              }
            })
            .catch((error) => {
              logger.error('Failed refreshing subscription claim after upgrade', sanitizeError(error), {
                userId: authUser.uid,
                expectedSubscription: plan,
              });
            });
        } else {
          logger.warn('Could not refresh subscription claim after upgrade because the auth user is unavailable', {
            expectedSubscription: plan,
          });
        }
      }

      if (!hasTrackedSuccess.current) {
        hasTrackedSuccess.current = true;
        try {
          const previousPlan = localStorage.getItem('previousPlanBeforePayment') ?? 'unknown';
          track(
            'Pricing - Payment_success',
            {
              context: 'Payments',
              paymentType,
              amount,
              plan,
              previousPlan,
              entrypoint: knownEntrypoint(urlParams.get('entrypoint')),
            },
            TrackTypeEnum.BI,
          );
          localStorage.removeItem('previousPlanBeforePayment');
        } catch (error) {
          try {
            logger.error('Failed tracking payment success', sanitizeError(error));
          } catch {
            // never block payment UX
          }
        }
      }
      if (paymentType !== PaymentType.UNKNOWN) {
        const successMessage = getSuccessMessage(paymentType, amount, plan);

        // Clean up the specific payment-related query params
        urlParams.delete('payment');
        urlParams.delete('session_id');
        navigate(`${window.location.pathname}?${urlParams.toString()}`, { replace: true });

        // Get the previous page URL from localStorage
        const previousPageUrl = localStorage.getItem('lastPageBeforePayment') || '/';

        try {
          let redirectPath: string;
          const { search } = new URL(previousPageUrl);
          if (plan === SubscriptionType.Team) {
            redirectPath = `/settings?section=team${search}`;
          } else {
            const { pathname } = new URL(previousPageUrl);
            redirectPath = pathname + search;
          }

          raiseSnackbar(successMessage);

          // Redirect to the previous page
          navigate(redirectPath);
        } catch (_error) {
          // Fallback if URL parsing fails
          navigate('/');
          raiseSnackbar(successMessage);
        }

        setTimeout(() => {
          // Clean up any remaining query params from the current URL after redirecting.
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.delete('payment');
          urlParams.delete('session_id');
          urlParams.delete('amount');
          urlParams.delete('plan');
          urlParams.delete('entrypoint');
          navigate(`${window.location.pathname}?${urlParams.toString()}`, { replace: true });
        }, 500);
      }
    } else if (urlParams.get('payment')) {
      // Delete the payment parameter if session_id is not present
      urlParams.delete('payment');
      urlParams.delete('amount');
      urlParams.delete('plan');
      urlParams.delete('entrypoint');
      const newUrl = urlParams.toString();
      navigate(`${window.location.pathname}?${newUrl}`, { replace: true });
    }
  }, [authApi, getSuccessMessage, navigate, raiseSnackbar, track]);

  return null;
}

export default PaymentSuccessHandler;
